import { AppLayout } from '@layouts/app-layout/AppLayout';
import { HeaderOnlyLayout } from '@layouts/header-only-layout/HeaderOnlyLayout';
import { ProfileLayout } from '@layouts/profile-layout/ProfileLayout';
import { AccessPassPage } from '@pages/access-pass/AccessPassPage';
import { AppInitializer } from '@pages/app-initializer/AppInitializer';
import { HomePage } from '@pages/home/HomePage';
import { ModuleSelectorPage } from '@pages/module-selector/ModuleSelectorPage';
import { OrganizationSelectorPage } from '@pages/organization-selector/OrganizationSelectorPage';
import { NotificationsRoute } from '@pages/profile/routes/notifications-route/NotificationsRoute';
import { PasswordAndSecurityRoute } from '@pages/profile/routes/password-and-security-route/PasswordAndSecurityRoute';
import { EditCitizenshipRoute } from '@pages/profile/routes/personal-information-route/edit-citizenship-route/EditCitizenshipRoute';
import { EditContactRoute } from '@pages/profile/routes/personal-information-route/edit-contact-route/EditContactRoute';
import { EditIdentityRoute } from '@pages/profile/routes/personal-information-route/edit-identity-route/EditIdentityRoute';
import { EditOtherInformationRoute } from '@pages/profile/routes/personal-information-route/edit-other-information-route/EditOtherInformationRoute';
import { PersonalInformationRoute } from '@pages/profile/routes/personal-information-route/PersonalInformationRoute';
import { ProfileEntryRoute } from '@pages/profile/routes/profile-entry-route/ProfileEntryRoute';
import { AuthenticatedRoute } from '@shared/components/route-guards/authenticated-route/AuthenticatedRoute';
import { FeatureFlagGuard } from '@shared/components/route-guards/feature-flag-guard/FeatureFlagGuard';
import { ModuleSelectorGuard } from '@shared/components/route-guards/module-selector-guard/ModuleSelectorGuard';
import { OrganizationSelectorGuard } from '@shared/components/route-guards/organization-selector-guard/OrganizationSelectorGuard';
import { PermissionGuard } from '@shared/components/route-guards/permission-guard/PermissionGuard';
import { VisitorGuard } from '@shared/components/route-guards/visitor-guard/VisitorGuard';
import { RouteObject } from 'react-router-dom';

import { APP_ROUTES, PROFILE_ROUTE_SEGMENTS } from './routes.constants';

/**
 * Common authenticated routes - not module-specific
 * Includes: App initializer, Selectors, Visitor home
 */
export const commonRoutes: RouteObject[] = [
  // App initializer - entry point after login
  {
    path: APP_ROUTES.APP_INITIALIZER,
    element: (
      <AuthenticatedRoute>
        <AppInitializer />
      </AuthenticatedRoute>
    ),
  },

  // Module selector - only for users with multiple modules and no selection yet
  {
    path: APP_ROUTES.MODULE_SELECTOR,
    element: (
      <AuthenticatedRoute>
        <ModuleSelectorGuard>
          <ModuleSelectorPage />
        </ModuleSelectorGuard>
      </AuthenticatedRoute>
    ),
  },

  // Organization selector - only for Manage users with multiple orgs and no selection yet
  {
    path: APP_ROUTES.ORGANIZATION_SELECTOR,
    element: (
      <AuthenticatedRoute>
        <OrganizationSelectorGuard>
          <OrganizationSelectorPage />
        </OrganizationSelectorGuard>
      </AuthenticatedRoute>
    ),
  },
  {
    path: APP_ROUTES.ACCESS_PASS,
    element: (
      <AuthenticatedRoute>
        <FeatureFlagGuard flag="accessPageEnabledWebApp">
          <PermissionGuard
            permission={['AccessPassWithTraining_View_Own', 'AccessPassNoTraining_View_Own']}
            operator="some"
          >
            <AppLayout />
          </PermissionGuard>
        </FeatureFlagGuard>
      </AuthenticatedRoute>
    ),
    children: [
      {
        index: true,
        element: <AccessPassPage />,
      },
    ],
  },
  // Common flow home (Visitor only - users with no modules)
  {
    path: APP_ROUTES.HOME,
    element: (
      <AuthenticatedRoute>
        <VisitorGuard />
      </AuthenticatedRoute>
    ),
    children: [
      {
        element: (
          <PermissionGuard permission="BasicHome_View_Any">
            <AppLayout />
          </PermissionGuard>
        ),
        children: [
          {
            index: true,
            element: <HomePage />,
          },
        ],
      },
    ],
  },

  {
    path: APP_ROUTES.PROFILE,
    element: (
      <AuthenticatedRoute>
        <PermissionGuard
          permission={[
              'ProfilePersonalBasic_FullAccess_Own',
              'ProfilePersonalTraining_FullAccess_Own',
              'ProfileNotifications_FullAccess_Own',
              'ProfilePassword_FullAccess_Own',
            ]}
            operator="some"
          >
            <HeaderOnlyLayout>
              <ProfileLayout />
            </HeaderOnlyLayout>
        </PermissionGuard>
      </AuthenticatedRoute>
    ),
    children: [
      {
        index: true,
        element: <ProfileEntryRoute />,
      },
      {
        path: PROFILE_ROUTE_SEGMENTS.PERSONAL_INFORMATION,
        element: (
          <PermissionGuard
            permission={[
              'ProfilePersonalBasic_FullAccess_Own',
              'ProfilePersonalTraining_FullAccess_Own',
            ]}
            operator="some"
          >
            <PersonalInformationRoute />
          </PermissionGuard>
        ),
        children: [
          {
            path: PROFILE_ROUTE_SEGMENTS.EDIT_IDENTITY,
            element: <EditIdentityRoute />,
          },
          {
            path: PROFILE_ROUTE_SEGMENTS.EDIT_CONTACT,
            element: <EditContactRoute />,
          },
          {
            path: PROFILE_ROUTE_SEGMENTS.EDIT_OTHER_INFORMATION,
            element: <EditOtherInformationRoute />,
          },
          {
            path: PROFILE_ROUTE_SEGMENTS.EDIT_CITIZENSHIP,
            element: (
              <PermissionGuard permission="ProfilePersonalTraining_FullAccess_Own">
                <EditCitizenshipRoute />
              </PermissionGuard>
            ),
          },
        ],
      },
      {
        path: PROFILE_ROUTE_SEGMENTS.NOTIFICATIONS,
        element: (
          <PermissionGuard
            permission={[
              'ProfileNotifications_FullAccess_Own',
            ]}
            operator="some"
          >
            <NotificationsRoute />
          </PermissionGuard>
        ),
      },
      {
        path: PROFILE_ROUTE_SEGMENTS.PASSWORD_AND_SECURITY,
        element: (
          <PermissionGuard permission="ProfilePassword_FullAccess_Own">
            <PasswordAndSecurityRoute />
          </PermissionGuard>
        ),
      },
    ],
  },
];
